import { useCallback, useEffect, useState } from "react";

/**
 * Captura o evento `beforeinstallprompt` do navegador para permitir um botão
 * "Instalar app" customizado dentro do PharuERP.
 *
 * Retorna:
 *  - podeInstalar:  true quando o navegador detectou que o PWA pode ser instalado.
 *  - foiInstalado:  true quando o usuário já confirmou instalação (ou abriu em modo standalone).
 *  - instalar():    dispara o prompt nativo do navegador.
 *
 * Suportado em Chrome, Edge e demais Chromium. Safari iOS NÃO dispara
 * `beforeinstallprompt` — o usuário precisa usar "Adicionar à tela de início".
 */
export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [foiInstalado, setFoiInstalado] = useState(() => {
    if (typeof window === "undefined") return false;
    if (!window.matchMedia) return false;
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.matchMedia("(display-mode: window-controls-overlay)").matches ||
      window.navigator.standalone === true // iOS
    );
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleBeforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };
    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setFoiInstalado(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const instalar = useCallback(async () => {
    if (!deferredPrompt) return { outcome: "no-prompt" };
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice && choice.outcome === "accepted") {
        setDeferredPrompt(null);
        setFoiInstalado(true);
      }
      return choice || { outcome: "dismissed" };
    } catch (err) {
      return { outcome: "error", error: err };
    }
  }, [deferredPrompt]);

  return {
    podeInstalar: Boolean(deferredPrompt) && !foiInstalado,
    foiInstalado,
    instalar,
  };
}

export default useInstallPrompt;
